/**
 * Vietnamese words seen in Sapo product titles whose accents are plain
 * Latin-1 (á í à) or absent, which the Vietnamese-letter pass in
 * displayTitle cannot detect. Garment and colour words only — extend when a
 * new one leaks onto the English or Thai storefront.
 */
const VN_PLAIN_WORDS = ["Áo", "Loe", "Dài", "Tím", "Túi", "Váy", "Mút", "Thao", "Tay", "Có", "Cho", "Bao", "Lì", "Xì", "Giá", "Phí"];
const VN_PLAIN_WORDS_RE = new RegExp(
  `\\s*(?<![\\p{L}\\p{N}])(?:${VN_PLAIN_WORDS.join("|")})(?![\\p{L}\\p{N}])`,
  "giu",
);

/**
 * Locale-aware product title display.
 *
 * Production product titles follow the per-color-product naming pattern:
 *   "Celebré Classic Skirt - Strawberry (Đỏ Dâu)"
 *
 * The trailing parenthetical is the Vietnamese translation of the colour.
 * On the English storefront we want to drop it so customers don't see
 * mixed-language strings; on Vietnamese we keep the original title intact.
 *
 * The product row also has a separate `titleEn` column populated by Sapo
 * import — when present it's the canonical English form and should win.
 * This helper accepts both shapes so callers can pass whatever they have:
 *
 *   displayTitle("Foo - Red (Đỏ)", "en")              → "Foo - Red"
 *   displayTitle("Foo - Red (Đỏ)", "en", "Foo - Red") → "Foo - Red"
 *   displayTitle("Foo - Red (Đỏ)", "vi")              → "Foo - Red (Đỏ)"
 *
 * Pure / synchronous / no Remix imports → safe in client event handlers,
 * SSR loaders, and email templates alike.
 */
export function displayTitle(
  title: string,
  locale: string | null | undefined,
  titleEn?: string | null,
): string {
  // Defensive: collapse an accidental leading "The The " (data-entry dup
  // observed on 4 Monaco Skorts products 2026-06-07) → "The ". Storefront
  // display fix; the source titles should also be corrected in Sapo/admin.
  const collapseThe = (s: string) => s.replace(/^The\s+The\s+/i, "The ");
  const safe = collapseThe((title ?? "").trim());
  if (!safe) return "";

  // English locale: prefer the curated `titleEn` column; otherwise strip
  // the trailing ` (Vietnamese)` parenthetical from the raw title.
  // Thai chrome (5b) reads product names like EN — curated titleEn, English
  // colour words — until the partner's per-product Thai overrides land
  // (those overwrite BOTH title fields, so they win in either branch).
  if (locale === "en" || locale === "th") {
    if (titleEn && titleEn.trim()) return collapseThe(titleEn.trim());
    // No curated titleEn: strip Vietnamese parentheticals ANYWHERE in the
    // title — Sapo's bilingual convention puts them mid-string too
    // ("Sculpt - Black (Đen) - Bra"). Detected by diacritics so legit
    // Latin parens like "(Limited)" or "(Pro)" survive. Then also strip
    // a trailing parenthetical (original behavior).
    const stripped = safe
      .replace(/\s*\([^)]*[\u00C0-\u024F\u1E00-\u1EF9][^)]*\)/gu, "")
      .replace(/\s*\([^)]*\)\s*$/u, "")
      // Bare Vietnamese words outside parentheses ("Standard Leggings
      // Thường", "Hàng lẻ") — TH feedback #2. The character class is
      // VIETNAMESE-specific (ă â ê ô ơ ư đ + the 1EA0-1EF9 tone block),
      // deliberately excluding plain Latin-1 accents so "Celebré" and
      // other French-flavoured line names survive.
      .replace(/\s*\S*[ăâêôơưđĂÂÊÔƠƯĐ\u1EA0-\u1EF9]\S*/gu, "")
      // …and Vietnamese title words that carry no Vietnamese-specific
      // letter, so the pass above leaves them behind ("Flare Leggings
      // Ống Loe" → "Flare Leggings Loe", "Áo Bra Grace" — TH feedback
      // row 10). A fixed word list, matched as whole words, so English
      // and French-flavoured names ("Celebré", "Taupe") are untouched.
      .replace(VN_PLAIN_WORDS_RE, "")
      .replace(/\s{2,}/g, " ")
      .replace(/\s+-\s*$/u, "")
      .replace(/^\s*-\s+/u, "")
      .trim();
    return stripped || safe;
  }

  // Vietnamese (and any other non-EN locale): swap the English colour
  // word for the Vietnamese translation that lives in the trailing
  // parenthetical. Sapo's convention is `<Base> - <EnColour> (<ViColour>)`
  // — e.g. "Femme Bra - Black (Đen)". On the VN site we want to show
  // "Femme Bra - Đen", not the bilingual mash-up. Merchant clarified
  // 2026-05-01: "english colors should be in english and vietnamese
  // color should display only in vietnamese when that language is set".
  //
  // The regex captures: <prefix> " - " <enColour-or-piece> " (" <viPart> ")"
  // anchored to end of string. Anything mid-title (e.g. "(Limited)") is
  // ignored — the parenthetical must be the LAST thing in the title.
  // If the title doesn't match the pattern (no VN colour available),
  // fall back to the raw value so we never lose information.
  const viMatch = safe.match(/^(.*) - ([^()]*?) \(([^()]+)\)\s*$/u);
  if (viMatch) {
    const [, prefix, , viColour] = viMatch;
    const trimmedVi = viColour.trim();
    if (trimmedVi) return `${prefix.trim()} - ${trimmedVi}`;
  }
  return safe;
}

/**
 * Apply EQL-specific product title rewrites.
 *
 * Sapo names some products with internal pattern words that aren't great
 * for the customer-facing storefront. Rather than overwrite the Sapo
 * source-of-truth title in the DB (which would also alter the field that
 * Shop-the-Look matching uses for token extraction — see the loader at
 * _storefront.products.$handle.tsx), we rewrite for display only.
 *
 * Current rules:
 *
 *   1. Monaco-specific renames (merchant override):
 *        "Monaco Skorts" / "Monaco Skirts" → "The Monaco Skorts"
 *        "Monaco Bra"   / "Monaco Bras"    → "The Monaco Top"
 *
 *      Merchant clarified 2026-05-25: the bottom is a *skort* (skirt
 *      with built-in shorts) — earlier "Skirt" rename collapsed the
 *      term and shoppers searching "skort" weren't finding the product.
 *      Pricing distinguishes the two pieces: Top = 500k, Skorts = 550k.
 *
 *   2. " Set" disambiguation. Several lines have a SET product whose
 *      title is just "<Line> - <Color>" alongside companion piece
 *      products (Bra/Top/Skirt/Pants/etc.) — on the homepage / list
 *      cards the bare set title looks identical to the line name and
 *      customers can't tell what they're looking at. For those lines
 *      we insert " Set" between the line name and the dash.
 *
 *      Audit 2026-05-01 identified the following lines as needing
 *      disambiguation:
 *        Ace, Aura, Cozy, Courtney, Femme Long, Muse, Smash, Whisper
 *
 *      Detection: title starts with "<Line> - " AND has no piece
 *      keyword anywhere (Bra / Top / Skirt / Skorts / Pants / Jacket
 *      / etc.). Pieces always carry the keyword in the Sapo title;
 *      sets never do.
 *
 * Applied at the loader edge, AFTER token extraction for Shop-the-Look,
 * BEFORE the response is serialised. Pure string transform — keeps the
 * trailing colour suffix and the Vietnamese parenthetical intact.
 */

// Piece keyword regex — copied / aligned with productPieceClassifier.ts's
// TITLE_PIECE_RE so a piece detected on either side gets the same answer.
// Matches whole words only (\b) so "Topaz" doesn't trigger on "top".
const PIECE_KEYWORD_RE =
  /\b(top|short|shorts|skort|skorts|skirt|bottom|legging|leggings|tights|tank|bra|jacket|pant|pants|cami|tee|halter|pullover|capri|visor|sock|socks)\b/i;

// Lines where the bare "<Line> - <Color>" SKU is a SET that needs the
// " Set" disambiguator. Listed longest-prefix-first so "Femme Long" is
// matched before any future "Femme" entry could shadow it.
const SET_DISAMBIGUATION_LINES: ReadonlyArray<string> = [
  "Femme Long",
  "Recharge 2.0",
  "Focus 3.0",
  "Ace",
  "Aura",
  "Cozy",
  "Courtney",
  "Muse",
  "Smash",
  "Whisper",
  // 14 Oct 2026: names now follow Sapo, which dropped the trailing "Set" on
  // these lines' set products ("Comfort - Cherry (Đỏ) Set" → "Comfort -
  // Cherry (Đỏ)"). Keeps the website saying "Set" for them.
  "Align",
  "Balance",
  "Bare",
  "Comfort",
  "Crush",
  "Curve",
  "Ease",
  "Fleur",
  "Form",
  "Momentum",
  "Perform",
  "Recharge",
  "Refocus",
  "Shape",
  "Slam",
  "Spin",
  "Zest",
];

// Lines whose bare "<Line> - <Color>" product is a DRESS (Sapo dropped the
// trailing "Dress" with the same rename).
const DRESS_DISAMBIGUATION_LINES: ReadonlyArray<string> = ["Finesse", "Game"];

// Lines whose SET products carry a piece word in the Sapo name — the bottom
// that comes with the set ("Impulse 2.0 - Black (Đen) - Short (Quần Đùi)" is
// the shorts SET) or the line name itself ("Femme Shorts - Navy"). The piece
// check below would skip them, so they are labelled first.
const SET_LINES_NAMED_BY_PIECE: ReadonlyArray<string> = ["Impulse 2.0", "Impulse 3.0", "Femme Shorts"];

const escapeRe = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

export function renameProductTitle(title: string | null | undefined): string {
  if (!title) return title || "";
  let t = title;

  // 1) Monaco-specific renames. Single regex per target so the output
  //    of the first replace can't be re-matched on a second pass —
  //    e.g. a sequential `Skorts → The Monaco Skorts` then `Skirts? →
  //    The Monaco Skorts` would produce "The The Monaco Skorts".
  t = t
    .replace(/\bMonaco\s+(?:Skorts|Skirts?)\b/gi, "The Monaco Skorts")
    .replace(/\bMonaco\s+Bras?\b/gi, "The Monaco Top")
    // FIX 2026-06-07: when a SOURCE title already begins with "The Monaco
    // Skorts" (merchant pre-named it, or a prior rename was persisted back to
    // Sapo), the rename above re-prefixes "The" → "The The Monaco Skorts"
    // (seen on 4 products: Lemon / Blueberry / Strawberry Pink / Espresso).
    // The single-regex-per-target guard only stops Skorts↔Skirts doubling,
    // not already-prefixed input — collapse any doubled "The" definitively.
    .replace(/\bThe\s+The\b/gi, "The");

  // 2) " Set" disambiguation. Skip if the title already names a piece
  //    (Bra / Top / Skirt / etc.) — only the bare set title needs help.
  //    Also skip when the title already says "Set" ANYWHERE: Sapo now
  //    carries both conventions ("Courtney - Cobalt (Xanh Coban Đậm) Set"
  //    has the trailing word) and re-inserting produced the double-labeled
  //    "Courtney Set - Cobalt (Xanh Coban Đậm) Set" (reported 2026-08-05).
  if (!/\bsets?\b/i.test(t)) {
    for (const line of SET_LINES_NAMED_BY_PIECE) {
      const re = new RegExp(`^${escapeRe(line)}\\s*-\\s*`, "i");
      if (re.test(t)) return t.replace(re, `${line} Set - `);
    }
  }
  if (!PIECE_KEYWORD_RE.test(t) && !/\bsets?\b|\bdress(es)?\b/i.test(t)) {
    for (const line of DRESS_DISAMBIGUATION_LINES) {
      const re = new RegExp(`^${escapeRe(line)}\\s*-\\s*`, "i");
      if (re.test(t)) return t.replace(re, `${line} Dress - `);
    }
    for (const line of SET_DISAMBIGUATION_LINES) {
      // Word-boundary anchored at start: "Cozy - " matches but
      // "Cozy Pants - " (handled by the piece check above anyway) and
      // "DiscoCozy - " do not.
      const re = new RegExp(`^${escapeRe(line)}\\s*-\\s*`, "i");
      if (re.test(t)) {
        t = t.replace(re, `${line} Set - `);
        break;
      }
    }
  }

  return t;
}
